import { useCallback } from 'react'
import { Check, ChevronLeft, ChevronRight } from 'lucide-react'
import { SimpleDropdown, SimpleDropdownItem } from '../ui/SimpleDropdown'
import { cn } from '../../lib/utils'

interface NavigatorItem {
  label?: string
}

export interface ItemNavigatorProps {
  items: NavigatorItem[]
  activeIndex: number
  onSelect: (index: number) => void
  size?: 'sm' | 'md'
}

export function ItemNavigator({ items, activeIndex, onSelect, size = 'sm' }: ItemNavigatorProps) {
  const goToPrev = useCallback(() => {
    onSelect(Math.max(0, activeIndex - 1))
  }, [onSelect, activeIndex])

  const goToNext = useCallback(() => {
    onSelect(Math.min(items.length - 1, activeIndex + 1))
  }, [onSelect, activeIndex, items.length])

  if (items.length <= 1) return null

  const activeItem = items[activeIndex]
  const displayLabel = activeItem?.label || `${activeIndex + 1} / ${items.length}`

  return (
    <div className="flex items-center gap-1 select-none">
      <button
        onClick={goToPrev}
        disabled={activeIndex === 0}
        className={cn(
          'bg-background shadow-minimal cursor-pointer',
          'text-foreground/50 hover:text-foreground transition-colors',
          'disabled:opacity-30 disabled:cursor-not-allowed',
          size === 'md' ? 'p-1.5 rounded-[8px]' : 'p-1 rounded-[6px]'
        )}
        title="Previous item"
      >
        <ChevronLeft className={size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
      </button>

      <SimpleDropdown
        align="start"
        trigger={(
          <button
            className={cn(
              'flex items-center text-muted-foreground font-medium',
              'bg-background shadow-minimal cursor-pointer',
              'hover:opacity-80 transition-opacity',
              size === 'md'
                ? 'text-[13px] px-3 h-[28px] w-[144px] justify-center rounded-[8px]'
                : 'text-[12px] px-2.5 h-[22px] w-[112px] justify-center rounded-[6px]'
            )}
            title="Select item"
          >
            <span className="truncate max-w-[120px]">{displayLabel}</span>
          </button>
        )}
      >
        {items.map((item, idx) => (
          <SimpleDropdownItem
            key={`${idx}-${item.label || ''}`}
            onClick={() => onSelect(idx)}
          >
            <span className="flex flex-1 items-center justify-between gap-2 truncate">
              <span className="truncate">{item.label || `Item ${idx + 1}`}</span>
              {idx === activeIndex ? <Check className="w-3.5 h-3.5 text-accent" /> : null}
            </span>
          </SimpleDropdownItem>
        ))}
      </SimpleDropdown>

      <button
        onClick={goToNext}
        disabled={activeIndex === items.length - 1}
        className={cn(
          'bg-background shadow-minimal cursor-pointer',
          'text-foreground/50 hover:text-foreground transition-colors',
          'disabled:opacity-30 disabled:cursor-not-allowed',
          size === 'md' ? 'p-1.5 rounded-[8px]' : 'p-1 rounded-[6px]'
        )}
        title="Next item"
      >
        <ChevronRight className={size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
      </button>
    </div>
  )
}
